import depsloader from "../../../depsloader";

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
    this.requestId = 0;
  }

  cancelAutocomplete() {
    this.requestId += 1;
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state) {
      p.push(g.state);
    }

    if (g.country) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _geoaddress(l) {
    if (!l || !l.properties || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    const lng = l.geometry.coordinates[0];
    const lat = l.geometry.coordinates[1];
    if (typeof lat !== "number" || typeof lng !== "number"
      || !Number.isFinite(lat) || !Number.isFinite(lng)
      || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null;
    }

    const properties = l.properties;
    const geoaddress = {};

    if (properties.type == "city") {
      geoaddress["city"] = properties.name || "";
    } else if (properties.type == "district") {
      geoaddress["city"] = properties.city || "";
      geoaddress["district"] = properties.name || properties.district || "";
    } else if (properties.type == "house") {
      geoaddress["city"] = properties.city || "";
      geoaddress["district"] = properties.district || "";
      geoaddress["street"] = properties.street || "";
      if (properties.housenumber) {
        geoaddress["number"] = properties.housenumber;
      }
    } else if (properties.type == "street") {
      geoaddress["city"] = properties.city || "";
      geoaddress["district"] = properties.district || "";
      geoaddress["street"] = properties.name || "";
    } else if (properties.type == "locality") {
      geoaddress["city"] = properties.city || "";
      geoaddress["district"] = properties.district || "";
      if (properties.street) {
        geoaddress["street"] = properties.street;
      }
    } else if (properties.osm_key == "place"
      && ["city", "town", "village", "municipality"].includes(properties.osm_value || "")) {
      geoaddress["city"] = properties.name || "";
    } else if ((properties.osm_key == "place" && properties.osm_value == "suburb")
      || properties.osm_key == "boundary") {
      geoaddress["city"] = properties.city || "";
      geoaddress["district"] = properties.name || "";
    } else if (properties.osm_key == "highway") {
      geoaddress["city"] = properties.city || "";
      geoaddress["district"] = properties.district || "";
      geoaddress["street"] = properties.name || "";
    }

    if (properties.postcode) geoaddress["zip"] = properties.postcode;

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = properties.state;
    geoaddress["country"] = properties.country;

    if (!geoaddress["city"] && !geoaddress["street"]
      && !geoaddress["district"] && properties.name) {
      geoaddress["text"] = properties.name;
    } else {
      geoaddress["text"] = this._geoaddressText(geoaddress);
    }

    return geoaddress;
  }

  geocode(params) {
    const runtime = window.ScreenworkMapsRuntime || {};
    return this._request(runtime.geocoderUrl, params, runtime.geocoderKey).then(features => {
      return features.map(feature => this._geoaddress(feature)).filter(Boolean);
    });
  }

  autocomplete(q) {
    this.cancelAutocomplete();

    const params = new URLSearchParams();
    params.append("q", q);
    params.append("lang", "de");
    params.append("limit", 10);
    params.append("osm_tag", "!place:state");
    params.append("osm_tag", "!place:house");
    //params.append("osm_tag", "!boundary:protected_area");
    params.append("osm_tag", "place");
    params.append("osm_tag", "boundary");

    return this.geocode(params);
  }

  reverse(lat, lng) {
    if (typeof lat !== "number" || typeof lng !== "number"
      || !Number.isFinite(lat) || !Number.isFinite(lng)
      || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return Promise.resolve(null);
    }

    const latitude = lat;
    const longitude = lng;
    this.cancelAutocomplete();
    const runtime = window.ScreenworkMapsRuntime || {};
    return this._request(runtime.reverseGeocoderUrl, {
      lat: latitude,
      lon: longitude,
      lang: "de"
    }, runtime.geocoderKey)
      .then(features => features.length ? this._geoaddress(features[0]) : null)
      .catch(() => null);
  }

  _request(url, params, key) {
    if (!url || !key) {
      return Promise.resolve([]);
    }

    const source = this.axios.CancelToken.source();
    const requestId = ++this.requestId;
    this.axiosRequest = { source, requestId };

    if (params instanceof URLSearchParams) {
      params.set("key", key);
    } else {
      params.key = key;
    }

    return this.axios.get(url, {
      cancelToken: source.token,
      params,
      timeout: 25000
    }).then(res => {
      if (requestId !== this.requestId) {
        return [];
      }
      return res.data && Array.isArray(res.data.features) ? res.data.features : [];
    }).catch(error => {
      if (this.axios.isCancel(error)) {
        return [];
      }
      throw error;
    }).finally(() => {
      if (this.axiosRequest && this.axiosRequest.requestId === requestId) {
        this.axiosRequest = null;
      }
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
